// index.js (StopmonitorMapView)
import SoftBox from "components/SoftComponents/SoftBox";
import PropTypes from 'prop-types';
import StopmonitorMap from "components/Maps/StopmonitorMap";
import { useEffect, useMemo, useState, useRef, useCallback } from "react"; // 1. Added useCallback
import { setDeviceFeatures, enableMap } from "utils/map";

import bksAPI from "services/api/bks";
import groupAPI from "services/api/groups";

import mapboxgl from '!mapbox-gl';
import { useSoftUIController } from "hooks/context";
import StopmonitorSidebar from "components/Sidenavs/StopmonitorSidebar";
import { processGroupData } from "utils/group";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function StopmonitorMapView({ map, user, devices, inspections, mapOptions, selectedViewDevice }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions } = controller;

    useEffect(() => {
        if (!map.current) return;
        enableMap(map, user, selectedOrg);
        map.current.setPadding({ left: 0, right: 0, top: 0, bottom: 0 });
    }, [selectedOrg]);

    groupAPI.loadToken(useAuth0());
    bksAPI.loadToken(useAuth0());

    const queryClient = useQueryClient();

    const [selectedDevice, setSelectedDevice] = useState(null);
    const [mapOption, setMapOption] = useState(null);

    const [isAttentionMode, setIsAttentionMode] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [pendingUpdates, setPendingUpdates] = useState({});

    const hasFittedBounds = useRef(false);
    const handledViewDevice = useRef(null);
    const handledMapOption = useRef(null);
    const set_padding = 200;

    const showAdvancedDeviceLevel = userPermissions.includes('devices-advanced:view');

    const groups = useQuery({
        queryKey: ['getAllGroups', selectedOrg],
        queryFn: async () => {
            return await groupAPI.getAllGroups();
        }
    });

    const groups_data = useMemo(() => {
        if (typeof groups.data != 'undefined') {
            return groups.data.filter(g => !g.is_dev).map(function (group) {
                return processGroupData(groups.dataUpdatedAt, group, devices);
            });
        }
        return null;
    }, [groups.data, devices, selectedOrg]);

    const allDevicesBounds = useMemo(() => {
        if (!devices || devices.length === 0) return null;
        const bounds = new mapboxgl.LngLatBounds();

        devices.forEach(function (device) {
            const feature = setDeviceFeatures(device, showAdvancedDeviceLevel);
            if (feature?.geometry?.coordinates) {
                bounds.extend(feature.geometry.coordinates);
            }
        });

        // debugger;

        return bounds.isEmpty() ? null : bounds.toArray();
    }, [devices, showAdvancedDeviceLevel]);

    const attentionDevices = useMemo(() => {
        // If it's turned off, or data is missing, immediately return an empty array
        if (!isAttentionMode || !inspections) return [];

        return inspections.filter(i => i.status === "unresolved" || i.status === "review")
            .map(i => ({
                ...i.devices[0],
                title: i.title,
                status: i.status,
                position: [...i.location].reverse(), // Kept the array clone fix from earlier!
                hardware_type: null,
                active: false,
                images: i.images,
                organisation_id: i.organisation_id,
                description: i.description,
                removed: true,
                inspectionCreatedAt: i.createdAt
            }));
    }, [isAttentionMode, inspections]);

    useEffect(() => {
        hasFittedBounds.current = false;
        handledViewDevice.current = null;
        handledMapOption.current = null;
        setSelectedDevice(null);
    }, [selectedOrg]);


    useEffect(() => {
        if (mapOption == null) {
            setSelectedDevice(null);
        }
    }, [mapOption]);

    useEffect(() => {
        if (!map.current) return;

        // Clear the mapOption lock if the user clicks off a device, allowing them to select it again later
        if (!mapOption) handledMapOption.current = null;

        if (mapOption && ["Devices", "Sites", "Stops"].indexOf(mapOption?.category) > -1) {
            // Compare against a unique string rather than the whole object, as object references change on re-render
            const optionId = mapOption.uuid || mapOption.value;

            if (handledMapOption.current !== optionId) {
                handledMapOption.current = optionId;
                hasFittedBounds.current = true;

                requestAnimationFrame(() => {
                    if (map.current) {
                        map.current.stop();
                        map.current.flyTo({ center: [mapOption.position[0], mapOption.position[1]], zoom: 17, pitch: 40, bearing: 0, essential: true, speed: 1.7 });
                        setSelectedDevice(mapOption);
                    }
                });
            }
            return;
        }
        
        if (selectedViewDevice) {
            if (handledViewDevice.current !== selectedViewDevice.uuid) {
                handledViewDevice.current = selectedViewDevice.uuid;
                hasFittedBounds.current = true; // Lock immediately to prevent race conditions

                requestAnimationFrame(() => {
                    if (map.current) {
                        map.current.stop();
                        map.current.flyTo({ center: [selectedViewDevice.position[0], selectedViewDevice.position[1]], zoom: 17, pitch: 40, bearing: 0, essential: true, speed: 2 });
                        setSelectedDevice(selectedViewDevice);
                    }
                });
            }
            return;
        }

        if (hasFittedBounds.current) return;

        if (allDevicesBounds) {
            const options = {
                linear: true,
                padding: deviceLayout == 'mobile'
                    ? { top: 0, left: set_padding / 2, right: set_padding / 2, bottom: set_padding / 2 }
                    : { top: 0, left: set_padding, right: set_padding, bottom: set_padding },
                pitch: 40, bearing: 0, maxZoom: 19
            };

            hasFittedBounds.current = true;

            requestAnimationFrame(() => {
                if (map.current) {
                    map.current.stop();
                    map.current.fitBounds(allDevicesBounds, options);
                }
            });
        }
    }, [map, deviceLayout, selectedOrg, selectedViewDevice, allDevicesBounds, mapOption]);

    const handleLocationChange = useCallback((uuid, newLat, newLng) => {
        setPendingUpdates(prev => ({
            ...prev,
            [uuid]: { latitude: newLat, longitude: newLng }
        }));
    }, []);

    const handleSaveLocations = async () => {
        if (Object.keys(pendingUpdates).length === 0) {
            setIsEditMode(false);
            return;
        }

        try {
            await bksAPI.saveDeviceMapLocations(pendingUpdates);
            // console.log("Saving new coordinates:", pendingUpdates);

            queryClient.setQueryData(['getAllDevices', selectedOrg], (oldDevices) => {
                if (!oldDevices) return oldDevices;

                return oldDevices.map((device) => {
                    const update = pendingUpdates[device.uuid];
                    if (update) return { ...device, position: { ...update } };

                    return device;
                });
            });

            setPendingUpdates({});
            setIsEditMode(false);
        }
        catch (error) {
            console.error("Failed to save locations to the database:", error);
        }
    };

    return (
        <SoftBox sx={{ width: '100%', height: '100%' }}>
            <StopmonitorMap groups={groups_data || []} {...{ map, user, devices, attentionDevices, setSelectedDevice, mapOption, setMapOption, isAttentionMode, isEditMode, handleLocationChange }} />
            <StopmonitorSidebar inspections={inspections.filter(i => i.status === "unresolved" || i.status === "review")} {...{ map, user, selectedDevice, setSelectedDevice, mapOptions, mapOption, setMapOption, isAttentionMode, setIsAttentionMode, isEditMode, setIsEditMode, handleSaveLocations }} />
        </SoftBox>
    );
}

StopmonitorMapView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
    devices: PropTypes.array,
    inspections: PropTypes.object,
    mapOptions: PropTypes.array,
    selectedViewDevice: PropTypes.object
}

export default StopmonitorMapView;